"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UtilsManager = void 0;
const sdk_geometry_api_sdk_v2_1 = require("@shapediver/sdk.geometry-api-sdk-v2");
const viewer_shared_global_access_objects_1 = require("@shapediver/viewer.shared.global-access-objects");
const viewer_shared_node_tree_1 = require("@shapediver/viewer.shared.node-tree");
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
const viewer_shared_types_1 = require("@shapediver/viewer.shared.types");
const DraggingParameter_1 = require("../dto/interaction/DraggingParameter");
const GumballParameter_1 = require("../dto/interaction/GumballParameter");
const SelectionParameter_1 = require("../dto/interaction/SelectionParameter");
const Parameter_1 = require("../dto/Parameter");
const SessionTreeNode_1 = require("../SessionTreeNode");
/**
 * Manager responsible for utility functions.
 * This is the manager for miscellaneous utility functions that do not fit into other managers.
 *
 * The manager is created by the SessionEngineCore and can be accessed
 * via the `utilsManager` property.
 */
class UtilsManager {
    constructor(sessionEngineCore) {
        this._converter = viewer_shared_services_1.Converter.instance;
        this._eventEngine = viewer_shared_services_1.EventEngine.instance;
        this._globalAccessObjects = viewer_shared_global_access_objects_1.GlobalAccessObjects.instance;
        this._httpClient = viewer_shared_services_1.HttpClient.instance;
        this._logger = viewer_shared_services_1.Logger.instance;
        this._sceneTree = viewer_shared_node_tree_1.Tree.instance;
        this._stateEngine = viewer_shared_services_1.StateEngine.instance;
        this._retryCounter = 0;
        this._sessionEngineCore = sessionEngineCore;
    }
    /**
     * Add a busy mode to all viewport engines.
     * To remove the busy mode, use `removeBusyMode`.
     *
     * @param busyId The ID of the busy mode to add
     */
    addBusyMode(busyId) {
        for (const r in this._stateEngine.viewportEngines) {
            if (this._stateEngine.viewportEngines[r] &&
                !this._sessionEngineCore.excludeViewports.includes(r)) {
                this._stateEngine.viewportEngines[r].busy.push(busyId);
                this._sessionEngineCore.customizationManager.customizationBusyModes.push(busyId);
            }
        }
    }
    /**
     * Adds a node to the scene tree and updates the root version.
     *
     * @param node The tree node to add
     */
    addToSceneTree(node) {
        this._sceneTree.addNode(node);
        this._sceneTree.root.updateVersion();
    }
    /**
     * Cancels the current process if a new customization request has been made or if the session was closed.
     * If the process is cancelled, a TASK_CANCEL event is emitted and the busy mode is removed.
     *
     * @param eventInfo The task event information
     * @param customizationId The ID of the customization process
     * @param newNode The new tree node to return if the process is cancelled
     * @returns The new tree node if the process was cancelled, otherwise undefined
     */
    cancelProcess(eventInfo, customizationId, newNode = new SessionTreeNode_1.SessionTreeNode()) {
        if (this._sessionEngineCore.customizationManager
            .customizationProcess !== customizationId) {
            this.removeBusyMode(customizationId);
            this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_CANCEL, Object.assign(Object.assign({}, eventInfo), { status: "The request was exceeded by another customization request" }));
            this._logger.debug(`Session(${this._sessionEngineCore.id}).cancelProcess: The request was was exceeded by another request.`);
            return newNode;
        }
        else if (this._sessionEngineCore.closed === true) {
            this.removeBusyMode(customizationId);
            this._logger.debug(`Session(${this._sessionEngineCore.id}).cancelProcess: The session was closed during the request.`);
            this._eventEngine.emitEvent(viewer_shared_services_1.EVENTTYPE.TASK.TASK_CANCEL, Object.assign(Object.assign({}, eventInfo), { progress: 1, status: "The session was closed during the request." }));
            return new SessionTreeNode_1.SessionTreeNode();
        }
    }
    /**
     * Checks the availability of the session and optionally a specific action.
     *
     * @param action The action to check for availability
     * @param checkForModelId Whether to check for the model ID availability
     * @throws ShapeDiverViewerSessionError if the session or action is not available
     */
    checkAvailability(action, checkForModelId = false) {
        var _a;
        if (!this._sessionEngineCore.responseDto)
            throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.checkAvailability: responseDto not available.");
        if (!this._sessionEngineCore.sessionId)
            throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.checkAvailability: sessionId not available.");
        if (checkForModelId && !this._sessionEngineCore.modelId)
            throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.checkAvailability: modelId not available.");
        if (action && !this._sessionEngineCore.responseDto.actions)
            throw new viewer_shared_services_1.ShapeDiverViewerSessionError("Session.checkAvailability: actions not available.");
        const responseDtoAction = (_a = this._sessionEngineCore.responseDto.actions) === null || _a === void 0 ? void 0 : _a.find((a) => a.name === action);
        if (action && !responseDtoAction)
            throw new viewer_shared_services_1.ShapeDiverViewerSessionError(`Session.checkAvailability: action ${action} not available.`);
    }
    /**
     * Create an interaction parameter based on the parameter definition.
     *
     * @param parameter
     * @returns
     */
    createInteractionParameter(parameter) {
        const result = (0, viewer_shared_types_1.validateInteractionParameterSettings)(parameter.settings);
        if (result.success) {
            switch (parameter.settings.type) {
                case "selection":
                    return new SelectionParameter_1.SelectionParameter(parameter, this._sessionEngineCore, this._sessionEngineCore.parameterManager);
                case "gumball":
                    return new GumballParameter_1.GumballParameter(parameter, this._sessionEngineCore, this._sessionEngineCore.parameterManager);
                case "dragging":
                    return new DraggingParameter_1.DraggingParameter(parameter, this._sessionEngineCore, this._sessionEngineCore.parameterManager);
            }
        }
        else {
            this._logger.warn(`SessionEngine.createInteractionParameter: The value ${parameter.settings} is not a valid InteractionParameter: ${result.error.message}`);
        }
        return new Parameter_1.Parameter(parameter, this._sessionEngineCore, this._sessionEngineCore.parameterManager);
    }
    /**
     * Handles errors that occur during session operations.
     *
     * @param err The error to handle
     * @param retry Whether to retry the operation
     * @returns A promise that resolves when the error has been handled
     */
    handleError(err, retry = false) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(err instanceof Error))
                throw this._httpClient.convertError(err);
            // Process the error
            const e = yield (0, sdk_geometry_api_sdk_v2_1.processError)(err);
            if (e instanceof sdk_geometry_api_sdk_v2_1.ResponseError) {
                if (e.type === sdk_geometry_api_sdk_v2_1.ResErrorType.SESSION_GONE_ERROR) {
                    // case 1: the session is no longer available
                    // we try to re-initialize the session 3 times, if that does not work, we close it
                    this._logger.warn("The session has been closed, trying to re-initialize.");
                    if (this._sessionEngineCore.sessionId)
                        this._httpClient.removeDataLoading(this._sessionEngineCore.sessionId);
                    if (this._retryCounter < 3) {
                        // we retry this 3 times, the `retry` option in the init function is set to true and passed on
                        this._retryCounter = retry ? this._retryCounter + 1 : 1;
                        this._sessionEngineCore.initialized = false;
                        yield this._sessionEngineCore.init(this._sessionEngineCore.parameterManager
                            .parameterValues, true);
                    }
                    else {
                        // the retries were exceeded, we close the session
                        this._logger.warn("Tried to retry the connect multiple times, bearer token still not valid. Closing Session.");
                        // eslint-disable-next-line no-empty
                        try {
                            yield this._sessionEngineCore.closeOnFailure();
                        }
                        catch (e) {
                            /* empty */
                        }
                        throw this._httpClient.convertError(e);
                    }
                }
                else if (e.type === sdk_geometry_api_sdk_v2_1.ResErrorType.JWT_VALIDATION_ERROR) {
                    // if any of the above errors occur, we try to get a new bearer token
                    // if we get a new one, we retry 3 times (by requiring new bearer tokens every time)
                    if (this._retryCounter < 3) {
                        if (this._sessionEngineCore.refreshJwtToken) {
                            yield this._sessionEngineCore.setJwtToken(yield this._sessionEngineCore.refreshJwtToken());
                            this._retryCounter = retry ? this._retryCounter + 1 : 1;
                            this._logger.warn("Re-trying with new bearer token.");
                        }
                        else {
                            // no bearer tokens are supplied, we close the session
                            this._logger.warn("No retry possible, no new bearer token was supplied. Closing Session.");
                            // eslint-disable-next-line no-empty
                            try {
                                yield this._sessionEngineCore.closeOnFailure();
                            }
                            catch (e) {
                                /* empty */
                            }
                            throw this._httpClient.convertError(e);
                        }
                    }
                    else {
                        // the retries were exceeded, we close the session
                        this._logger.warn("Tried to retry the connect multiple times, bearer token still not valid. Closing Session.");
                        // eslint-disable-next-line no-empty
                        try {
                            yield this._sessionEngineCore.closeOnFailure();
                        }
                        catch (e) {
                            /* empty */
                        }
                        throw this._httpClient.convertError(e);
                    }
                }
                else {
                    throw this._httpClient.convertError(e);
                }
            }
            else {
                throw this._httpClient.convertError(e);
            }
        });
    }
    /**
     * Process the image input and return the image data and array buffer.
     *
     * In the case of the image being a Blob or File, the image data is constructed from the Blob or File.
     * In the case of the image being a string, we check if it is a data URL or a URL.
     * If it is a data URL, we convert it to a Blob and construct the image data from the Blob.
     * If it is a URL, we download the image and return the image data and array buffer.
     *
     * @param image
     * @returns
     */
    processImageInput(image) {
        return __awaiter(this, void 0, void 0, function* () {
            if (image instanceof File || image instanceof Blob)
                return this._converter.constructImageData(image);
            let imageString;
            if (image instanceof Promise) {
                imageString = yield image;
            }
            else if (typeof image === "function") {
                const result = image();
                if (result instanceof Promise) {
                    imageString = yield result;
                }
                else {
                    imageString = result;
                }
            }
            else {
                imageString = image;
            }
            if (imageString.startsWith("data:")) {
                // case where the image is a data URL
                const { blob, arrayBuffer } = this._converter.dataURLtoBlob(imageString);
                return {
                    imageData: {
                        format: blob.type,
                        size: blob.size,
                    },
                    arrayBuffer: arrayBuffer,
                };
            }
            else {
                // case where the image is a URL
                const response = yield new sdk_geometry_api_sdk_v2_1.UtilsApi(this._sessionEngineCore.sdkConfig).downloadImage(this._sessionEngineCore.sessionId, imageString, {
                    responseType: "arraybuffer",
                });
                const arrayBuffer = response.data;
                return {
                    imageData: {
                        format: response.headers["content-type"],
                        size: arrayBuffer.byteLength,
                    },
                    arrayBuffer,
                };
            }
        });
    }
    removeBusyMode(busyId) {
        for (const r in this._stateEngine.viewportEngines) {
            if (this._stateEngine.viewportEngines[r] &&
                this._stateEngine.viewportEngines[r].busy.includes(busyId))
                this._stateEngine.viewportEngines[r].busy.splice(this._stateEngine.viewportEngines[r].busy.indexOf(busyId), 1);
            if (this._sessionEngineCore.customizationManager.customizationBusyModes.includes(busyId))
                this._sessionEngineCore.customizationManager.customizationBusyModes.splice(this._sessionEngineCore.customizationManager.customizationBusyModes.indexOf(busyId), 1);
        }
    }
    /**
     * Removes a node from the scene tree and updates the root version.
     *
     * @param node The tree node to remove
     */
    removeFromSceneTree(node) {
        this._sceneTree.removeNode(node);
        this._sceneTree.root.updateVersion();
    }
    /**
     * Returns a promise that resolves after the amount of milliseconds provided.
     *
     * @param ms the milliseconds
     * @returns promise that resolve after specified milliseconds
     */
    timeout(ms) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => setTimeout(resolve, ms));
        });
    }
    /**
     * Waits for all update callbacks to finish.
     *
     * @param newOutputVersions The new output versions
     * @param oldOutputVersions The old output versions
     * @param newNode The new tree node
     * @param oldNode The old tree node
     */
    waitForUpdateCallbacks(newOutputVersions, oldOutputVersions, newNode, oldNode) {
        return __awaiter(this, void 0, void 0, function* () {
            // call the update callback function on the session
            if (this._sessionEngineCore.updateCallback)
                yield Promise.resolve(this._sessionEngineCore.updateCallback(newNode, oldNode));
            const promises = [];
            // call the update callback functions on the outputs
            for (const outputId in this._sessionEngineCore.outputManager.outputs) {
                if (oldOutputVersions[outputId] !== newOutputVersions[outputId]) {
                    promises.push(this._sessionEngineCore.outputManager.outputs[outputId].triggerUpdateCallback(newNode.children.find((c) => c.name === outputId), oldNode.children.find((c) => c.name === outputId)));
                }
            }
            yield Promise.all(promises);
        });
    }
    /**
     * Creates warnings for outputs and exports with issues.
     * If `throwError` is true, a ShapeDiverViewerCustomizationError is thrown instead.
     *
     * @param outputs The outputs to check for issues
     * @param exports The exports to check for issues
     * @param throwError Whether to throw an error instead of creating warnings
     */
    warningCreator(outputs, exports, throwError = false) {
        const outputsWithIssues = {};
        const exportsWithIssues = {};
        for (const outputId in outputs) {
            const outputObj = outputs[outputId];
            if ((throwError === false && outputObj.msg !== undefined) ||
                (outputObj.status_collect &&
                    outputObj.status_collect !==
                        sdk_geometry_api_sdk_v2_1.ResComputationStatus.SUCCESS) ||
                (outputObj.status_computation &&
                    outputObj.status_computation !==
                        sdk_geometry_api_sdk_v2_1.ResComputationStatus.SUCCESS)) {
                outputsWithIssues[outputId] = outputObj;
            }
        }
        for (const exportId in exports) {
            const exportObj = exports[exportId];
            if ((throwError === false && exportObj.msg !== undefined) ||
                (exportObj.status_collect &&
                    exportObj.status_collect !==
                        sdk_geometry_api_sdk_v2_1.ResComputationStatus.SUCCESS) ||
                (exportObj.status_computation &&
                    exportObj.status_computation !==
                        sdk_geometry_api_sdk_v2_1.ResComputationStatus.SUCCESS)) {
                exportsWithIssues[exportId] = exportObj;
            }
        }
        if (Object.keys(outputsWithIssues).length > 0 ||
            Object.keys(exportsWithIssues).length > 0) {
            if (throwError) {
                throw new viewer_shared_services_1.ShapeDiverViewerCustomizationError("There was at least one output or export with issues.", { outputs: outputsWithIssues, exports: exportsWithIssues });
            }
            else {
                // create warning messages for outputs
                for (const outputId in outputsWithIssues) {
                    let warning = "";
                    if (outputsWithIssues[outputId].msg)
                        warning += `\n\t- ${outputsWithIssues[outputId].msg}`;
                    if (outputsWithIssues[outputId].status_collect &&
                        outputsWithIssues[outputId].status_collect !==
                            sdk_geometry_api_sdk_v2_1.ResComputationStatus.SUCCESS)
                        warning += `\n\t- status_collect is ${outputsWithIssues[outputId].status_collect}`;
                    if (outputsWithIssues[outputId].status_computation &&
                        outputsWithIssues[outputId].status_computation !==
                            sdk_geometry_api_sdk_v2_1.ResComputationStatus.SUCCESS)
                        warning += `\n\t- status_computation is ${outputsWithIssues[outputId].status_computation}`;
                    if (warning)
                        this._logger.warn(`\nOutput(${outputId}):${warning}`);
                }
                // create warning messages for exports
                for (const exportId in exportsWithIssues) {
                    let warning = "";
                    if (exportsWithIssues[exportId].msg)
                        warning += `\n\t- ${exportsWithIssues[exportId].msg}`;
                    if (exportsWithIssues[exportId].status_collect &&
                        exportsWithIssues[exportId].status_collect !==
                            sdk_geometry_api_sdk_v2_1.ResComputationStatus.SUCCESS)
                        warning += `\n\t- status_collect is ${exportsWithIssues[exportId].status_collect}`;
                    if (exportsWithIssues[exportId].status_computation &&
                        exportsWithIssues[exportId].status_computation !==
                            sdk_geometry_api_sdk_v2_1.ResComputationStatus.SUCCESS)
                        warning += `\n\t- status_computation is ${exportsWithIssues[exportId].status_computation}`;
                    if (warning)
                        this._logger.warn(`\nExport(${exportId}):${warning}`);
                }
            }
        }
    }
}
exports.UtilsManager = UtilsManager;
//# sourceMappingURL=UtilsManager.js.map